import type { CrewlinkClient } from '../supabase-client';
import type { SolConversationRow } from '../database.types';

/**
 * Writer wrappers for the conversation-history writers of migration 0023
 * (M11 / D-041): archive (reversible) and delete (genuine).
 *
 * The layering rule (ARCHITECTURE §2, INV-T02): UI → query hooks → writer
 * wrappers → Postgres RPC. UI code never calls `.rpc(...)` directly and never
 * calls `.update(...)` or `.delete(...)` at all. Ownership is not a parameter —
 * it comes from `auth.uid()` inside the function (INV-T23).
 *
 * **THESE TWO ARE THE SCREEN'S OWN WRITERS, BEHIND THE SCREEN'S OWN
 * CONFIRMATION.** The Sol read endpoint cannot reach either of them — the
 * structural guard asserts it — so nothing the model says can archive or
 * delete a conversation. Only a tap here can.
 *
 * Delete carries D-040's memory rule inside the database function: automatic
 * memories sourced from the conversation die with it; explicit ones survive
 * with their provenance detached. The confirmation copy on the screen says so
 * before the tap (`sol-wording.ts`).
 */

export async function archiveSolConversation(
  client: CrewlinkClient,
  conversationId: string,
  archived: boolean,
): Promise<SolConversationRow> {
  const { data, error } = await client.rpc('archive_sol_conversation', {
    p_conversation_id: conversationId,
    p_archived: archived,
  });
  if (error) throw error;
  if (!data) throw new Error('archive_sol_conversation returned no row');
  return data;
}

export async function deleteSolConversation(
  client: CrewlinkClient,
  conversationId: string,
): Promise<void> {
  const { error } = await client.rpc('delete_sol_conversation', {
    p_conversation_id: conversationId,
  });
  if (error) throw error;
}

/** Turn a failure into something Brandon can act on. Never a code, never a UUID. */
export function describeConversationWriteError(message: string): string {
  const normalised = message.toLowerCase();
  if (normalised.includes('not found') || normalised.includes('p0002')) {
    return 'That conversation is no longer available.';
  }
  if (normalised.includes('not authenticated') || normalised.includes('42501')) {
    return 'Your session has expired. Sign in again and try once more.';
  }
  if (normalised.includes('failed to fetch') || normalised.includes('network')) {
    return 'Could not reach Crewlink. Check your connection and try again.';
  }
  return 'Could not change that conversation. Please try again.';
}
